import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AddProposal,
  type ProposalCreateRequest,
  type ProposalInvoice,
  REGISTRY_DATA_LABELS,
  type RegistryProposalsResponse,
} from '@k-management/shared';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { sortRows, type SortValue } from '@k-management/shared';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { treasuryKeys } from './treasury-ui.js';

type Tab = 'add' | 'link' | 'roles' | 'data' | 'aliases' | 'dismissed';

export const ROLE_NAMES: Record<'C' | 'F', string> = { C: 'Cliente', F: 'Fornitore' };

export const proposalKeys = ['treasury', 'registry-proposals'] as const;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** «2 attivi, 1 passivo», or the opening balance when there is nothing else. */
export function documentsOf(item: AddProposal): string {
  if (item.opening) {
    const side = item.passive ? 'a debito' : 'a credito';
    return item.invoices === 1 ? `saldo iniziale ${side}` : `${item.invoices} saldi iniziali`;
  }
  return [
    item.active ? plural(item.active, 'attivo', 'attivi') : null,
    item.passive ? plural(item.passive, 'passivo', 'passivi') : null,
  ]
    .filter(Boolean)
    .join(', ');
}

export function RolePills({ roles }: { roles: readonly string[] }) {
  return (
    <>
      {roles.map((role) => (
        <span key={role} className="pill info">
          {ROLE_NAMES[role as 'C' | 'F'] ?? role}
        </span>
      ))}
    </>
  );
}

/**
 * Tesoreria › Anagrafica dalle fatture (1.73, ADR-021): what the invoices
 * read from Gestify, and their electronic invoices, propose to the
 * Anagrafica — subjects missing, roles, data, aliases — confirmed one by one.
 * Nothing changes by itself; what is set aside comes back with a new value.
 * 1.77: subjects matched only by VAT number or tax code, roles from the
 * documents (passive → Fornitore, active → Cliente).
 */
export function RegistryProposalsPage() {
  usePageTitle('Anagrafica dalle fatture');
  const queryClient = useQueryClient();
  const seesRegistry = useCan('anagrafica.read');
  const [tab, setTab] = useState<Tab>('add');
  const [creating, setCreating] = useState<AddProposal | null>(null);
  const [linking, setLinking] = useState<AddProposal | null>(null);
  const [done, setDone] = useState<{ text: string; subjectId: string | null } | null>(null);
  const data = useQuery({
    queryKey: proposalKeys,
    queryFn: () => apiRequest<RegistryProposalsResponse>('/treasury/registry-proposals'),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: proposalKeys });
    // The invoices may have a subject now, and the aliases reach the lists.
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
  };
  const action = useMutation({
    mutationFn: ({ path, key }: { path: string; key: string; text: string }) =>
      apiRequest<void>(`/treasury/registry-proposals/${path}`, {
        method: 'POST',
        body: { key },
      }),
    onSuccess: async (_result, { text }) => {
      setDone({ text, subjectId: null });
      await refresh();
    },
  });
  const allRoles = useMutation({
    mutationFn: () =>
      apiRequest<{ added: number }>('/treasury/registry-proposals/confirm-roles', {
        method: 'POST',
      }),
    onSuccess: async (result) => {
      setDone({ text: `Ruoli aggiunti: ${result.added}.`, subjectId: null });
      await refresh();
    },
  });
  const identifiers = (item: AddProposal) =>
    [item.vatNumber, item.taxCode].filter(Boolean).join(' · ') || null;
  const addSorting = useTableSort('registry-proposals.add', data.data?.add ?? [], {
    name: (item) => item.name,
    identifiers,
    documents: (item) => item.invoices,
    roles: (item) => item.roles.map((role) => ROLE_NAMES[role]).join(', '),
  });
  const linkValues: Record<string, (item: AddProposal) => SortValue> = {
    name: (item) => item.name,
    identifiers,
    documents: (item) => item.invoices,
    subject: (item) => item.subject?.name ?? null,
  };
  const linkSorting = useTableSort('registry-proposals.link', data.data?.link ?? [], linkValues);
  if (data.isPending) return <p className="muted">Caricamento…</p>;
  if (data.isError) return <p className="error">{data.error.message}</p>;
  const found = data.data;
  // Those without identifiers stay in their group below, in the same order.
  const manualRows = sortRows(
    found.manual,
    linkSorting.sort,
    linkSorting.sort ? linkValues[linkSorting.sort.key] : undefined,
  );
  const counts: Record<Tab, number> = {
    add: found.add.length,
    link: found.link.length + found.manual.length,
    roles: found.roles.length,
    data: found.data.length,
    aliases: found.aliases.length,
    dismissed: found.dismissed.length,
  };
  const labels: Record<Tab, string> = {
    add: 'Nuovi soggetti',
    link: 'Da collegare',
    roles: 'Ruoli dai documenti',
    data: 'Dati',
    aliases: 'Alias',
    dismissed: 'Ignorate',
  };
  const run = (path: 'confirm' | 'dismiss' | 'restore', key: string, text: string) => {
    setDone(null);
    action.mutate({ path, key, text });
  };
  const dismiss = (key: string) => (
    <button
      type="button"
      className="link"
      disabled={action.isPending}
      onClick={() => run('dismiss', key, 'Proposta ignorata.')}
    >
      Ignora
    </button>
  );
  const create = (item: AddProposal, primary: boolean) => (
    <button
      type="button"
      className={primary ? 'primary small' : 'small'}
      onClick={() => {
        setDone(null);
        setCreating(item);
      }}
    >
      Crea soggetto
    </button>
  );
  const link = (item: AddProposal, label: string, primary: boolean) => (
    <button
      type="button"
      className={primary ? 'primary small' : 'small'}
      onClick={() => {
        setDone(null);
        setLinking(item);
      }}
    >
      {label}
    </button>
  );
  const sameName = (item: AddProposal) =>
    item.sameName && (
      <small className="muted block">
        stesso nome di «{item.sameName.name}»
        {item.sameName.vatNumber ? ` (P. IVA ${item.sameName.vatNumber})` : ''}
        {item.subject || item.vatNumber || item.taxCode ? ': soggetti diversi' : ''}
      </small>
    );
  const linkRow = (item: AddProposal) => (
    <tr key={item.key}>
      <td>
        <strong>{item.name}</strong>
      </td>
      <td>{identifiers(item) ?? '—'}</td>
      <td>
        {documentsOf(item)} · ultimo <InvoiceLink invoice={item.last} />
      </td>
      <td>
        {item.subject ? (
          <>
            <SubjectName id={item.subject.id} name={item.subject.name} />
            {' · '}
            {item.subject.roles.map((role) => ROLE_NAMES[role as 'C' | 'F'] ?? role).join(', ') ||
              'senza ruoli'}
            <span className="block">
              {item.newRoles.length ? (
                <span className="pill warn">
                  diventa anche {item.newRoles.map((role) => ROLE_NAMES[role]).join(' e ')}
                </span>
              ) : (
                <span className="pill ok">ruolo già presente</span>
              )}
            </span>
          </>
        ) : (
          (sameName(item) ?? <span className="muted">—</span>)
        )}
      </td>
      <td>
        <div className="row">
          {item.subject ? (
            link(item, 'Collega', true)
          ) : (
            <>
              {link(item, 'Collega a…', false)}
              {create(item, false)}
            </>
          )}
          {dismiss(item.key)}
        </div>
      </td>
    </tr>
  );
  const actions = (key: string, confirmLabel: string, text: string) => (
    <div className="row">
      <button
        type="button"
        className="primary small"
        disabled={action.isPending}
        onClick={() => run('confirm', key, text)}
      >
        {confirmLabel}
      </button>
      <button
        type="button"
        className="link"
        disabled={action.isPending}
        onClick={() => run('dismiss', key, 'Proposta ignorata.')}
      >
        Ignora
      </button>
    </div>
  );

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Anagrafica dalle fatture</h1>
          <p className="muted">
            Quello che le fatture lette da Gestify, e le loro fatture elettroniche, dicono dei
            soggetti. Niente cambia finché non confermi; ogni conferma resta nel registro attività
            del soggetto con la fattura da cui viene.
          </p>
        </div>
      </div>
      <div className="segmented-small" role="tablist" aria-label="Proposte">
        {(Object.keys(labels) as Tab[]).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'selected' : ''}
            onClick={() => setTab(item)}
          >
            {labels[item]} ({counts[item]})
          </button>
        ))}
      </div>
      {done && (
        <p className="notice">
          {done.text}
          {done.subjectId && seesRegistry && (
            <>
              {' '}
              <Link to={`/anagrafica/${done.subjectId}`}>Apri il soggetto</Link>
            </>
          )}
        </p>
      )}
      {action.error && <p className="error">{action.error.message}</p>}
      {allRoles.error && <p className="error">{allRoles.error.message}</p>}

      {tab === 'add' && (
        <div className="card stack">
          <p className="muted small">
            Partite IVA e codici fiscali dei documenti che nessun soggetto dell’Anagrafica ha. Il
            ruolo viene dai documenti: passivi → Fornitore, attivi → Cliente, tutti e due → entrambi
            i ruoli.
          </p>
          {found.add.length === 0 ? (
            <p className="muted">Nessun soggetto nuovo nei documenti.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortHeader sort={addSorting} column="name">
                      Nome sui documenti
                    </SortHeader>
                    <SortHeader sort={addSorting} column="identifiers">
                      Partita IVA / CF
                    </SortHeader>
                    <SortHeader sort={addSorting} column="documents">
                      Documenti
                    </SortHeader>
                    <SortHeader sort={addSorting} column="roles">
                      Diventa
                    </SortHeader>
                    <th>Dalla fattura elettronica</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {addSorting.rows.map((item) => (
                    <tr key={item.key}>
                      <td>
                        <strong>{item.name}</strong>
                        {sameName(item)}
                      </td>
                      <td>{identifiers(item) ?? '—'}</td>
                      <td>
                        {documentsOf(item)} · ultimo <InvoiceLink invoice={item.last} />
                      </td>
                      <td>
                        <RolePills roles={item.roles} />
                      </td>
                      <td className="details">
                        {item.opening ? (
                          <span className="muted">saldo iniziale, nessuna fattura elettronica</span>
                        ) : item.xmlRead ? (
                          <>
                            {[item.address, [item.postalCode, item.city].filter(Boolean).join(' ')]
                              .filter(Boolean)
                              .join(', ') || '—'}
                            {item.province && ` (${item.province})`}
                            {item.iban && <small className="muted block">IBAN {item.iban}</small>}
                          </>
                        ) : (
                          <span className="muted">fattura elettronica non ancora letta</span>
                        )}
                      </td>
                      <td>
                        <div className="row">
                          {create(item, true)}
                          {dismiss(item.key)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted small">
            «Crea soggetto» apre la scheda già compilata, la inserisce in Anagrafica (con il
            controllo dei doppioni) e le collega i documenti.
          </p>
        </div>
      )}

      {tab === 'link' && (
        <div className="card stack">
          <p className="muted small">
            In alto: la partita IVA o il codice fiscale c’è già in Anagrafica, con un altro nome.
            Sotto: documenti senza partita IVA né codice fiscale, da collegare a mano.
          </p>
          {counts.link === 0 ? (
            <p className="muted">Nessun documento da collegare.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortHeader sort={linkSorting} column="name">
                      Nome sui documenti
                    </SortHeader>
                    <SortHeader sort={linkSorting} column="identifiers">
                      Partita IVA / CF
                    </SortHeader>
                    <SortHeader sort={linkSorting} column="documents">
                      Documenti
                    </SortHeader>
                    <SortHeader sort={linkSorting} column="subject">
                      In Anagrafica
                    </SortHeader>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {linkSorting.rows.map(linkRow)}
                  {found.manual.length > 0 && (
                    <tr>
                      <td colSpan={5} className="muted small">
                        Senza partita IVA né codice fiscale
                      </td>
                    </tr>
                  )}
                  {manualRows.map(linkRow)}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted small">
            «Collega» assegna i documenti al soggetto: il nome sui documenti diventa un suo alias e,
            se gli manca, prende il ruolo che i documenti dicono (resta anche quello che ha).
          </p>
        </div>
      )}

      {tab === 'roles' && (
        <ProposalTable
          intro="Soggetti dell’Anagrafica con documenti dell’altro tipo: un cliente che ci manda fatture passive è anche fornitore, e viceversa. Un soggetto, due ruoli."
          toolbar={
            found.roles.length > 1 && (
              <button
                type="button"
                className="small"
                disabled={allRoles.isPending}
                onClick={() => {
                  setDone(null);
                  allRoles.mutate();
                }}
              >
                Aggiungi tutti ({found.roles.length})
              </button>
            )
          }
          id="roles"
          empty="Nessun ruolo da aggiungere."
          head={['Soggetto', 'In Anagrafica è', 'Documenti', 'Aggiunge', '']}
          rows={found.roles.map((item) => ({
            key: item.key,
            sort: [
              item.subjectName,
              item.roles.map((role) => ROLE_NAMES[role as 'C' | 'F'] ?? role).join(', ') || null,
              item.invoices,
              ROLE_NAMES[item.roleCode],
            ],
            cells: [
              <SubjectName key="s" id={item.subjectId} name={item.subjectName} />,
              item.roles.map((role) => ROLE_NAMES[role as 'C' | 'F'] ?? role).join(', ') || '—',
              <>
                {item.roleCode === 'F'
                  ? plural(item.invoices, 'passivo', 'passivi')
                  : plural(item.invoices, 'attivo', 'attivi')}{' '}
                · ultimo <InvoiceLink invoice={item.last} />
              </>,
              <span key="r" className="pill warn">
                {ROLE_NAMES[item.roleCode]}
              </span>,
              actions(
                item.key,
                `Aggiungi ${ROLE_NAMES[item.roleCode]}`,
                `${item.subjectName}: aggiunto il ruolo ${ROLE_NAMES[item.roleCode]}.`,
              ),
            ],
          }))}
        />
      )}

      {tab === 'data' && (
        <ProposalTable
          id="data"
          empty="Nessun dato da aggiornare."
          head={['Soggetto', 'Campo', 'In Anagrafica', 'Nella fattura', '']}
          rows={found.data.map((item) => ({
            key: item.key,
            sort: [
              item.subjectName,
              REGISTRY_DATA_LABELS[item.field],
              item.current ?? null,
              item.proposed,
            ],
            cells: [
              <>
                <SubjectName id={item.subjectId} name={item.subjectName} />
                <small className="muted block">
                  <InvoiceLink invoice={item.last} />
                </small>
              </>,
              REGISTRY_DATA_LABELS[item.field],
              item.current ?? <span className="muted">—</span>,
              <>
                <strong>{item.proposed}</strong>{' '}
                <span className={item.current ? 'pill warn' : 'pill ok'}>
                  {item.current ? 'diverso' : 'vuoto in Anagrafica'}
                </span>
              </>,
              actions(
                item.key,
                'Usa questo',
                `${item.subjectName}: ${REGISTRY_DATA_LABELS[item.field]} aggiornato.`,
              ),
            ],
          }))}
        />
      )}

      {tab === 'aliases' && (
        <ProposalTable
          id="aliases"
          empty="Nessun nome nuovo sulle fatture."
          head={['Soggetto', 'Nome sulla fattura', 'Fatture', '']}
          rows={found.aliases.map((item) => ({
            key: item.key,
            sort: [item.subjectName, item.alias, item.invoices],
            cells: [
              <SubjectName key="s" id={item.subjectId} name={item.subjectName} />,
              <strong key="a">{item.alias}</strong>,
              <>
                {item.invoices} · ultima <InvoiceLink invoice={item.last} />
              </>,
              actions(
                item.key,
                'Aggiungi come alias',
                `${item.subjectName}: «${item.alias}» aggiunto agli alias.`,
              ),
            ],
          }))}
        />
      )}

      {tab === 'dismissed' && (
        <ProposalTable
          id="dismissed"
          empty="Nessuna proposta ignorata."
          head={['Proposta', 'Ignorata', '']}
          rows={found.dismissed.map((item) => ({
            key: item.key,
            sort: [item.label, item.at],
            cells: [
              item.label,
              `${formatTimestamp(item.at)}${item.by ? ` · ${item.by}` : ''}`,
              <button
                key="r"
                type="button"
                className="link"
                disabled={action.isPending}
                onClick={() => run('restore', item.key, 'Proposta ripresa.')}
              >
                Riprendi
              </button>,
            ],
          }))}
        />
      )}

      {creating && (
        <CreateDialog
          proposal={creating}
          types={found.types}
          onClose={() => setCreating(null)}
          onSaved={async (subjectId) => {
            setCreating(null);
            setDone({ text: `${creating.name} aggiunto in Anagrafica.`, subjectId });
            await refresh();
          }}
        />
      )}
      {linking && (
        <LinkDialog
          proposal={linking}
          subjects={found.subjects}
          onClose={() => setLinking(null)}
          onSaved={async (subjectId) => {
            setLinking(null);
            setDone({ text: `Documenti di «${linking.name}» collegati.`, subjectId });
            await refresh();
          }}
        />
      )}
    </section>
  );
}

function ProposalTable({
  id,
  intro,
  toolbar,
  empty,
  head,
  rows,
}: {
  /** Which list, for the sort kept per list. */
  id: string;
  intro?: string;
  toolbar?: ReactNode;
  empty: string;
  head: string[];
  /** `sort`: what the first columns sort by, in their order; the others do not sort. */
  rows: { key: string; cells: ReactNode[]; sort: SortValue[] }[];
}) {
  const sortable = rows[0]?.sort.length ?? 0;
  const sorting = useTableSort(
    `registry-proposals.${id}`,
    rows,
    Object.fromEntries(
      Array.from({ length: sortable }, (_, index) => [
        String(index),
        (row: (typeof rows)[number]) => row.sort[index] ?? null,
      ]),
    ),
  );
  return (
    <div className="card stack">
      {(intro || toolbar) && (
        <div className="row between">
          {intro && <p className="muted small">{intro}</p>}
          {toolbar}
        </div>
      )}
      {rows.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                {head.map((label, index) => (
                  <SortHeader key={index} sort={sorting} column={String(index)}>
                    {label}
                  </SortHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorting.rows.map((row) => (
                <tr key={row.key}>
                  {row.cells.map((cell, index) => (
                    <td key={index}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function InvoiceLink({ invoice }: { invoice: ProposalInvoice }) {
  return (
    <Link to={`/tesoreria/fatture/${invoice.id}`}>
      {invoice.number} del {formatDate(invoice.date)}
    </Link>
  );
}

/** The subject, a link to the Anagrafica for who sees it. */
export function SubjectName({ id, name }: { id: string; name: string }) {
  const seesRegistry = useCan('anagrafica.read');
  return seesRegistry ? <Link to={`/anagrafica/${id}`}>{name}</Link> : <>{name}</>;
}

/** «Crea soggetto»: the subject as the documents say it, changeable; the roles are theirs. */
export function CreateDialog({
  proposal,
  types,
  onClose,
  onSaved,
}: {
  proposal: AddProposal;
  types: { code: string; description: string }[];
  onClose: () => void;
  onSaved: (subjectId: string) => Promise<void>;
}) {
  const [form, setForm] = useState({
    typeCode: proposal.typeCode,
    name: proposal.name,
    country: proposal.country,
    vatNumber: proposal.vatNumber ?? '',
    taxCode: proposal.taxCode ?? '',
    address: proposal.address ?? '',
    postalCode: proposal.postalCode ?? '',
    city: proposal.city ?? '',
    province: proposal.province ?? '',
    iban: proposal.iban ?? '',
  });
  const { roles } = proposal;
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [key]: event.target.value });
  const save = useMutation({
    mutationFn: () =>
      apiRequest<{ subjectId: string }>('/treasury/registry-proposals/create', {
        method: 'POST',
        body: {
          key: proposal.key,
          ...form,
          country: form.country.trim().toUpperCase(),
          iban: roles.includes('F') ? form.iban : '',
        } satisfies ProposalCreateRequest,
      }),
    onSuccess: (result) => onSaved(result.subjectId),
  });
  return (
    <ConfirmDialog
      title="Crea il soggetto in Anagrafica"
      confirmLabel="Crea soggetto"
      busy={save.isPending}
      disabled={!form.name.trim()}
      error={save.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      <Field label="Tipologia">
        <select value={form.typeCode} onChange={set('typeCode')}>
          {types.map((type) => (
            <option key={type.code} value={type.code}>
              {type.description}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Nome">
        <input value={form.name} onChange={set('name')} maxLength={200} />
      </Field>
      <div className="row">
        <Field label="Nazione">
          <input value={form.country} onChange={set('country')} maxLength={2} size={3} />
        </Field>
        <Field label="Partita IVA">
          <input value={form.vatNumber} onChange={set('vatNumber')} maxLength={30} />
        </Field>
        <Field label="Codice fiscale">
          <input value={form.taxCode} onChange={set('taxCode')} maxLength={30} />
        </Field>
      </div>
      <Field label="Indirizzo">
        <input value={form.address} onChange={set('address')} maxLength={200} />
      </Field>
      <div className="row">
        <Field label="CAP">
          <input value={form.postalCode} onChange={set('postalCode')} maxLength={12} size={6} />
        </Field>
        <Field label="Comune">
          <input value={form.city} onChange={set('city')} maxLength={100} />
        </Field>
        <Field label="Provincia">
          <input value={form.province} onChange={set('province')} maxLength={40} size={4} />
        </Field>
      </div>
      <Field label="Ruoli">
        <div className="row">
          <RolePills roles={roles} />
          <small className="muted">dai documenti: {documentsOf(proposal)}</small>
        </div>
      </Field>
      {roles.includes('F') && (
        <Field label="IBAN del fornitore">
          <input value={form.iban} onChange={set('iban')} maxLength={40} />
        </Field>
      )}
      <p className="muted small">
        Gli altri dati (contatti, condizioni di pagamento…) si completano poi in Anagrafica.
      </p>
    </ConfirmDialog>
  );
}

/**
 * «Collega»: to the subject with the VAT number or tax code of the documents,
 * only confirmed; to one chosen when the documents carry neither.
 */
export function LinkDialog({
  proposal,
  subjects,
  onClose,
  onSaved,
}: {
  proposal: AddProposal;
  subjects: { id: string; name: string; vatNumber: string | null }[];
  onClose: () => void;
  onSaved: (subjectId: string) => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [subjectId, setSubjectId] = useState(proposal.subject?.id ?? '');
  const words = search.trim().toLowerCase();
  const shown = subjects
    .filter(
      (item) =>
        item.id === subjectId ||
        !words ||
        `${item.name} ${item.vatNumber ?? ''}`.toLowerCase().includes(words),
    )
    .slice(0, 200);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<void>('/treasury/registry-proposals/link', {
        method: 'POST',
        body: { key: proposal.key, subjectId },
      }),
    onSuccess: () => onSaved(subjectId),
  });
  const documents = `I documenti (${documentsOf(proposal)})`;
  const target = proposal.subject;
  const newRoles = target ? proposal.newRoles.map((role) => ROLE_NAMES[role]).join(' e ') : '';
  return (
    <ConfirmDialog
      title={
        target ? `Collegare «${proposal.name}» a «${target.name}»?` : `Collega «${proposal.name}»`
      }
      confirmLabel={newRoles ? `Collega e aggiungi ${newRoles}` : 'Collega'}
      busy={save.isPending}
      disabled={!subjectId}
      error={save.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      {target ? (
        <>
          <p>
            {proposal.vatNumber
              ? `Stessa partita IVA ${proposal.vatNumber}`
              : `Stesso codice fiscale ${proposal.taxCode}`}
            . {documents} vanno a questo soggetto e «{proposal.name}» diventa un suo alias.
          </p>
          {newRoles && (
            <p className="notice warn">
              In Anagrafica è{' '}
              {target.roles.map((role) => ROLE_NAMES[role as 'C' | 'F'] ?? role).join(' e ') ||
                'senza ruoli'}
              : i documenti gli aggiungono il ruolo <strong>{newRoles}</strong> (resta anche quello
              che ha).
            </p>
          )}
        </>
      ) : (
        <>
          <Field label="Cerca il soggetto">
            <input
              type="search"
              value={search}
              placeholder="Nome o partita IVA"
              onChange={(event) => setSearch(event.target.value)}
            />
          </Field>
          <Field label="Soggetto">
            <select
              value={subjectId}
              onChange={(event) => setSubjectId(event.target.value)}
              size={8}
            >
              {shown.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.vatNumber ? ` · ${item.vatNumber}` : ''}
                </option>
              ))}
            </select>
          </Field>
          <p className="muted small">
            {documents} vanno al soggetto scelto, «{proposal.name}», se diverso, diventa un suo
            alias e il soggetto prende i ruoli dei documenti che gli mancano.
          </p>
        </>
      )}
    </ConfirmDialog>
  );
}
